// Site Footer Web Component
import { appendReturnTo } from '../utils/return-to-url.js';

class SiteFooter extends HTMLElement {
  constructor() {
    super();
    this.currentYear = new Date().getFullYear();
  }

  connectedCallback() {
    this.render();
    this.startYearUpdater();
    this._updateDocsLinkBound = this.updateDocsLink.bind(this);
    document.addEventListener('route-changed', this._updateDocsLinkBound);
    document.addEventListener('section-shown', this._updateDocsLinkBound);
    this.updateDocsLink();
  }

  disconnectedCallback() {
    document.removeEventListener('route-changed', this._updateDocsLinkBound);
    document.removeEventListener('section-shown', this._updateDocsLinkBound);
    if (this.yearUpdateInterval) {
      clearInterval(this.yearUpdateInterval);
    }
  }

  render() {
    this.innerHTML = `
      <footer class="main-footer">
        <div class="container">
          <div class="footer-content">
            <div class="footer-main">
              <p class="footer-copyright">
                &copy; <span id="copyright-year">${this.currentYear}</span> Fun Old Times - Bringing back the fun of social networking
              </p>
            </div>
            <div class="footer-extras">
              <span class="footer-emoji">✨</span>
              <span class="footer-tagline">Built with ❤️ for the fun of it!</span>
            </div>
            <div class="footer-links" aria-label="Helpful links">
              <a href="/docs">Docs</a>
              <span aria-hidden="true">•</span>
              <a href="/privacy">Privacy Policy</a>
              <span aria-hidden="true">•</span>
              <a href="/terms">Terms & Conditions</a>
            </div>
          </div>
        </div>
      </footer>
    `;
  }

  updateDocsLink() {
    const docsLink = this.querySelector('.footer-links a[href^="/docs"]');
    if (!docsLink) {
      return;
    }

    const currentPath = window.location.pathname;
    if (currentPath === '/docs' || currentPath.startsWith('/docs/')) {
      docsLink.href = '/docs';
      return;
    }

    docsLink.href = appendReturnTo('/docs');
  }

  startYearUpdater() {
    // Check for year change every minute
    this.yearUpdateInterval = setInterval(() => {
      this.updateYear();
    }, 60000); // Check every minute

    // Also check on visibility change (when user returns to tab)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.updateYear();
      }
    });
  }

  updateYear() {
    const newYear = new Date().getFullYear();
    const yearElement = this.querySelector('#copyright-year');

    if (newYear !== this.currentYear && yearElement) {
      this.currentYear = newYear;

      // Add animation class
      yearElement.classList.add('year-updated');

      // Update the year
      yearElement.textContent = this.currentYear;

      // Remove animation class after animation completes
      setTimeout(() => {
        yearElement.classList.remove('year-updated');
      }, 600);

      // Special New Year's celebration
      this.celebrateNewYear();
      window.logger.info(`🎉 Happy New Year! Updated copyright to ${this.currentYear}`);
    }
  }

  celebrateNewYear() {
    // Create temporary celebration elements
    const celebration = document.createElement('div');
    celebration.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 3rem;
      z-index: 10000;
      pointer-events: none;
      animation: newYearCelebration 3s ease-out forwards;
    `;
    celebration.innerHTML = '🎉 Happy New Year! 🎉';

    // Add celebration styles
    const style = document.createElement('style');
    style.textContent = `
      @keyframes newYearCelebration {
        0% { 
          opacity: 0; 
          transform: translate(-50%, -50%) scale(0.5); 
        }
        20% { 
          opacity: 1; 
          transform: translate(-50%, -50%) scale(1.2); 
        }
        80% { 
          opacity: 1; 
          transform: translate(-50%, -50%) scale(1); 
        }
        100% { 
          opacity: 0; 
          transform: translate(-50%, -50%) scale(0.8); 
        }
      }
    `;

    document.head.appendChild(style);
    document.body.appendChild(celebration);

    // Create confetti effect
    this.createConfetti();

    // Clean up after animation
    setTimeout(() => {
      document.body.removeChild(celebration);
      document.head.removeChild(style);
    }, 3000);
  }

  createConfetti() {
    const confettiColors = ['#FFD700', '#FF6B9D', '#00BFFF', '#FF69B4', '#32CD32'];
    const confettiEmojis = ['🎉', '✨', '🎊', '🌟', '💫'];

    for (let i = 0; i < 20; i++) {
      setTimeout(() => {
        const confetti = document.createElement('div');
        const isEmoji = Math.random() > 0.5;

        confetti.style.cssText = `
          position: fixed;
          top: -10px;
          left: ${Math.random() * 100}vw;
          font-size: ${isEmoji ? '1.5rem' : '8px'};
          z-index: 9999;
          pointer-events: none;
          animation: confettiFall 3s linear forwards;
        `;

        if (isEmoji) {
          confetti.textContent = confettiEmojis[Math.floor(Math.random() * confettiEmojis.length)];
        } else {
          confetti.style.backgroundColor = confettiColors[Math.floor(Math.random() * confettiColors.length)];
          confetti.style.width = '8px';
          confetti.style.height = '8px';
          confetti.style.borderRadius = '50%';
        }

        // Add confetti animation
        if (!document.querySelector('#confetti-styles')) {
          const confettiStyle = document.createElement('style');
          confettiStyle.id = 'confetti-styles';
          confettiStyle.textContent = `
            @keyframes confettiFall {
              to {
                transform: translateY(100vh) rotate(360deg);
                opacity: 0;
              }
            }
          `;
          document.head.appendChild(confettiStyle);
        }

        document.body.appendChild(confetti);

        // Clean up confetti
        setTimeout(() => {
          if (confetti.parentNode) {
            confetti.parentNode.removeChild(confetti);
          }
        }, 3000);
      }, i * 100);
    }
  }
}

// Register the custom element
customElements.define('site-footer', SiteFooter);
